import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit } from "react-icons/fa";
import { useDispatch } from "react-redux";

import { ReactComponent as TeacherPlaceholder } from "../../../assets/Group318.svg";
import { ReactComponent as StudentPlaceholder } from "../../../assets/Group320.svg";
import { ReactComponent as ClassroomPlaceholder } from "../../../assets/Group322.svg";
import user_placeholder from "../../../assets/user-profile.jpg";
import Button from "../../../components/Button";
import EditLoggedUserModal from "../../../components/modals/common/EditLoggedUserModal";
import TruncatedTextWithTooltip from "../../../components/TruncatedTextWithTooltip";
import { USER_TYPE } from "../../../constants/common";
import useAuth from "../../../hooks/useAuth";
import { setUser } from "../../../redux/slices/userSlice";
import authServices from "../../../services/authServices";
import schoolServices from "../../../services/schoolServices";
import { useNavigateWithLang } from "../../../utils/navigation";

const SchoolDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithLang();
  const dispatch = useDispatch();
  const user = useAuth();
  const editLoggedUserModalRef = useRef();

  const [counts, setCounts] = useState({
    teachers_count: 0,
    classroom_count: 0,
    students_count: 0,
    students_limit: 0,
    active_teachers_count: 0,
    inActive_teachers_count: 0,
  });

  const openEditLoggedUserModal = () => editLoggedUserModalRef.current.openModal(user);

  const handleSave = async () => {
    const res = await authServices.me();
    const updatedUser = {
      ...user,
      name: res.name,
      username: res.username,
      email: res.email,
      phone: res.phone,
      img_uri: res.img_uri,
    };
    dispatch(setUser(updatedUser));
  };

  const getSchoolStats = async () => {
    try {
      const response = await schoolServices.getSchoolStats();
      setCounts({
        teachers_count: response.active_teachers_count || 0,
        classroom_count: response.classroom_count || 0,
        students_count: response.students_count || 0,
        students_limit: response.students_limit || 0,
        active_teachers_count: response.active_teachers_count || 0,
        inActive_teachers_count: response.inActive_teachers_count || 0,
      });
    } catch (error) {
      console.error("Error fetching school data:", error);
    }
  };

  useEffect(() => {
    getSchoolStats();
  }, []);

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
      {/* User Info Card */}
      <div className="col-span-1 rounded-xl bg-white p-6 shadow-lg dark:bg-dark-surface md:col-span-3 lg:col-span-2">
        <div className="flex items-center justify-end">
          <Button
            variant="text"
            icon={<FaEdit size={20} />}
            onClick={openEditLoggedUserModal}
            className="!px-0 hover:bg-transparent"
          />
        </div>
        <div className="mb-5 flex w-full flex-grow items-center gap-6 lg:mb-0">
          <img
            className="h-28 w-28 rounded-full border-4 border-blue-500 object-cover"
            src={user.img_uri || user_placeholder}
            alt="User"
          />
          <div className="flex-1">
            <TruncatedTextWithTooltip
              text={user?.school?.name}
              maxLength={20}
              className="text-xl font-semibold text-text dark:text-white ltr:text-start rtl:text-end"
            />
            <p className="text-sm capitalize text-mutedText dark:text-dark-mutedText">{user?.name}</p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {[
        {
          label: t("active_teachers"),
          count: counts.teachers_count,
          icon: <TeacherPlaceholder className="h-12 w-12 text-blue-500" />,
          link: `/${USER_TYPE.SCHOOL.ROLE}/teachers`,
        },
        {
          label: t("classrooms"),
          count: counts.classroom_count,
          icon: <ClassroomPlaceholder className="h-12 w-12 text-blue-500" />,
          link: `/${USER_TYPE.SCHOOL.ROLE}/classrooms`,
        },
        {
          label: t("student_accounts"),
          count: counts.students_count,
          icon: <StudentPlaceholder className="h-12 w-12 text-blue-500" />,
          link: `/${USER_TYPE.SCHOOL.ROLE}/students`,
          extra: (
            <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t("out_of")} {counts.students_limit}
            </span>
          ),
        },
      ].map((item, index) => (
        <div
          key={index}
          onClick={() => navigate(item.link)}
          className="flex cursor-pointer flex-col items-center justify-center rounded-xl bg-white p-6 shadow-md transition-transform duration-200 hover:scale-[1.03] dark:bg-dark-surface"
        >
          {item.icon}
          <p className="mt-3 text-3xl font-bold text-blue-500">{item.count}</p>
          <p className="mt-1 text-center font-semibold text-gray-700 dark:text-white">{item.label}</p>
          {item.extra}
        </div>
      ))}

      <EditLoggedUserModal ref={editLoggedUserModalRef} onSubmit={handleSave} />
    </div>
  );
};

export default SchoolDashboard;
